import type { Dielec, Varianta, Zostava } from "@/api/schemas";

/**
 * Čo sa má v tabuľke označiť (KOLO 233). User: *„k materiálu alebo hrane
 * alebo kovaniu zobraziť / označiť — označí všetky prvky, v akých
 * riadkoch sa nachádzajú"*.
 */
export type CielOznacenia =
  | { druh: "material"; slotId: string }
  | { druh: "hrana"; materialId: string }
  | { druh: "kovanie"; materialId: string };

export interface Oznacenie {
  /** Riadky na výber — vnorený riadok (vrstva, operácia) cez rodiča. */
  dielce: string[];
  /** Rodičia, ktorých vnorené riadky treba rozbaliť (zhoda je vo vrstve). */
  rozbalitDielce: string[];
  /** Koľko zostáv sa týka — do hlášky. */
  pocetZostav: number;
}

/** Má dielec hranu na niektorej strane (X1–Y2)? */
function maHranuNaStrane(d: Dielec, id: string): boolean {
  return (["x1", "x2", "y1", "y2"] as const).some(
    (s) => (d as Record<string, unknown>)[`hrana_${s}_id`] === id,
  );
}

/**
 * Má dielec hranu na strane, ako cenovku alebo medzi pridanými?
 *
 * To isté pravidlo, podľa ktorého Vymeniť hranu hľadá dotknuté dielce
 * (KOLO 169) — vytknuté z `VymenaDialog` (KOLO 233), aby označenie
 * a výmena hľadali tie isté riadky.
 */
export function maHranu(d: Dielec, id: string): boolean {
  return (
    maHranuNaStrane(d, id) ||
    (!!d.riadok_z_hrany && d.material_id_direct === id) ||
    (d.pridane_hrany ?? []).some((h) => h.material_id === id)
  );
}

function zhoda(d: Dielec, ciel: CielOznacenia): boolean {
  switch (ciel.druh) {
    case "material":
      return d.material_slot_id === ciel.slotId;
    case "hrana":
      // Masív hranu na stranách nemá (zaoblenie, KOLO 168) — ostatok id
      // po zmene materiálu na masív by označil riadok, kde hrana nie je
      return (
        (!d.masiv && maHranuNaStrane(d, ciel.materialId)) ||
        (!!d.riadok_z_hrany && d.material_id_direct === ciel.materialId) ||
        (d.pridane_hrany ?? []).some((h) => h.material_id === ciel.materialId)
      );
    case "kovanie":
      return d.material_id_direct === ciel.materialId;
  }
}

/** Riadky varianty, kde sa materiál, hrana alebo kovanie nachádza. */
export function riadkyNaOznacenie(
  varianta: Varianta,
  ciel: CielOznacenia,
): Oznacenie {
  const dielce: string[] = [];
  const rozbalit: string[] = [];
  let pocetZostav = 0;
  for (const z of (varianta.zostavy ?? []) as Zostava[]) {
    let vZostave = false;
    for (const d of (z.dielce ?? []) as Dielec[]) {
      if (!zhoda(d, ciel)) continue;
      vZostave = true;
      const id = d.parent_dielec_id ?? d.id;
      if (!dielce.includes(id)) dielce.push(id);
      if (d.parent_dielec_id && !rozbalit.includes(d.parent_dielec_id)) {
        rozbalit.push(d.parent_dielec_id);
      }
    }
    if (vZostave) pocetZostav += 1;
  }
  return { dielce, rozbalitDielce: rozbalit, pocetZostav };
}
